import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Activity, TrendingUp, TrendingDown, Shield, Wallet, BarChart3, Package, RefreshCw } from 'lucide-react';
import { RiyalIcon } from '@/lib/currency';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { exportMetricsToPDF } from '@/lib/pdfExport';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, PolarRadiusAxis } from 'recharts';

const statusColors = { good: 'bg-success', warning: 'bg-warning', danger: 'bg-error', neutral: 'bg-stone-400' };
const statusBg = { good: 'bg-success/10 border-success/30', warning: 'bg-warning/10 border-warning/30', danger: 'bg-error/10 border-error/30', neutral: 'bg-muted/50 border-border' };
const statusText = { good: 'text-success', warning: 'text-warning', danger: 'text-error', neutral: 'text-muted-foreground' };
const statusLabel = { good: 'Healthy', warning: 'Caution', danger: 'Critical', neutral: '—' };

const RatioCard = ({ name, value, unit, status, description, benchmark }) => (
  <div className={`rounded-xl border p-4 transition-all hover:shadow-md ${statusBg[status]}`} data-testid={`ratio-${name.replace(/\s/g, '-').toLowerCase()}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{name}</span>
      <Badge className={`text-[10px] ${statusColors[status]} text-white`}>{statusLabel[status]}</Badge>
    </div>
    <p className={`text-3xl font-bold ${statusText[status]}`}>{value}{unit || ''}</p>
    {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
    {benchmark && <div className="mt-2 h-1.5 bg-muted rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${statusColors[status]}`} style={{ width: `${Math.min(100, Math.max(5, typeof value === 'number' ? (value / benchmark.good) * 50 : 50))}%` }} />
    </div>}
  </div>
);

export default function FinancialRatiosPage() {
  const { t } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/accounting/financial-ratios');
      setData(res.data);
    } catch { toast.error('Failed to load financial ratios'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const r = data?.ratios || {};
  const raw = data?.raw_data || {};

  // Radar chart data
  const radarData = data ? [
    { metric: 'Liquidity', value: Math.min(100, (r.current_ratio?.value || 0) * 30) },
    { metric: 'Profitability', value: Math.min(100, Math.max(0, (r.net_profit_margin?.value || 0) * 3)) },
    { metric: 'Efficiency', value: Math.min(100, (r.asset_turnover?.value || 0) * 40) },
    { metric: 'Leverage', value: Math.min(100, Math.max(0, 100 - (r.debt_to_equity?.value || 0) * 30)) },
    { metric: 'Cash', value: Math.min(100, (r.cash_ratio?.value || 0) * 80) },
  ] : [];

  // Overall health score
  const healthScores = data ? Object.values(r).map(v => v.status === 'good' ? 3 : v.status === 'warning' ? 2 : v.status === 'danger' ? 1 : 2) : [];
  const avgHealth = healthScores.length ? (healthScores.reduce((a, b) => a + b, 0) / healthScores.length) : 0;
  const overallStatus = avgHealth >= 2.5 ? 'good' : avgHealth >= 1.5 ? 'warning' : 'danger';

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto" data-testid="financial-ratios-page">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-600 flex items-center justify-center shadow-lg shadow-violet-200/50">
              <Activity size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground">
                {t('financial_ratios_title') || 'Financial Health Dashboard'}
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">Key business metrics and benchmarks</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw size={14} className="mr-1" /> Refresh</Button>
            <button
              onClick={() => {
                const metrics = Object.entries(r).map(([key, v]) => ({
                  label: (v.name || key).replace(/_/g, ' '),
                  value: `${v.value ?? '-'}${v.unit || ''}`,
                  benchmark: v.benchmark_good ? `≥ ${v.benchmark_good}` : '-',
                  status: (v.status || '-').toUpperCase(),
                }));
                exportMetricsToPDF('Financial Health Dashboard', 'Key business metrics and benchmarks', metrics);
              }}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-muted-foreground bg-white border border-border rounded-lg hover:bg-muted/50 transition-colors"
              data-testid="export-ratios-pdf-btn"
            >
              <RefreshCw size={14} /> Export PDF
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : data && (
          <div id="financial-ratios-content">
            {/* Overall Health + Radar */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className={`col-span-1 border-2 ${statusBg[overallStatus]}`}>
                <CardContent className="p-6 text-center">
                  <div className={`w-20 h-20 rounded-full mx-auto mb-3 flex items-center justify-center ${statusColors[overallStatus]}`}>
                    <Shield size={36} className="text-white" />
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">Overall Business Health</p>
                  <p className={`text-2xl font-bold ${statusText[overallStatus]}`} data-testid="overall-health">{statusLabel[overallStatus]}</p>
                  <p className="text-xs text-muted-foreground mt-2">{Math.round(avgHealth / 3 * 100)}% Score</p>
                </CardContent>
              </Card>

              <Card className="col-span-1">
                <CardHeader className="pb-0"><CardTitle className="text-sm">Health Radar</CardTitle></CardHeader>
                <CardContent className="p-2" data-testid="health-radar">
                  <ResponsiveContainer width="100%" height={200}>
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#e7e5e4" />
                      <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11 }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} />
                      <Radar dataKey="value" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.25} strokeWidth={2} />
                    </RadarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card className="col-span-1">
                <CardHeader className="pb-2"><CardTitle className="text-sm">Balance Sheet Summary</CardTitle></CardHeader>
                <CardContent className="space-y-2 text-sm">
                  {[
                    { label: 'Cash & Bank', value: raw.cash_balance, color: 'text-success' },
                    { label: 'Accounts Receivable', value: raw.accounts_receivable, color: 'text-info' },
                    { label: 'Inventory', value: raw.inventory_value, color: 'text-warning' },
                    { label: 'Fixed Assets', value: raw.fixed_assets, color: 'text-purple-600' },
                    { label: 'Accounts Payable', value: -raw.accounts_payable, color: 'text-error' },
                    { label: 'Debt', value: -raw.total_debt, color: 'text-error' },
                    { label: 'Equity', value: raw.total_equity, color: 'text-violet-700' },
                  ].map(item => (
                    <div key={item.label} className="flex justify-between items-center py-1 border-b border-border">
                      <span className="text-muted-foreground">{item.label}</span>
                      <span className={`font-mono font-medium ${item.color}`}>{item.value?.toLocaleString()}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Liquidity Ratios */}
            <div>
              <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                <Wallet size={16} className="text-info" /> Liquidity Ratios
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <RatioCard name="Current Ratio" value={r.current_ratio?.value} unit="x" status={r.current_ratio?.status} description="Current Assets / Current Liabilities" benchmark={data.benchmarks?.current_ratio} />
                <RatioCard name="Quick Ratio" value={r.quick_ratio?.value} unit="x" status={r.quick_ratio?.status} description="(Current Assets - Inventory) / Liabilities" benchmark={data.benchmarks?.quick_ratio} />
                <RatioCard name="Cash Ratio" value={r.cash_ratio?.value} unit="x" status={r.cash_ratio?.status} description="Cash / Current Liabilities" benchmark={data.benchmarks?.cash_ratio} />
                <RatioCard name="Working Capital" value={r.working_capital?.value?.toLocaleString()} status={r.working_capital?.value >= 0 ? 'good' : 'danger'} description="Current Assets - Current Liabilities" />
              </div>
            </div>

            {/* Profitability Ratios */}
            <div>
              <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                <TrendingUp size={16} className="text-success" /> Profitability Ratios
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <RatioCard name="Gross Margin" value={r.gross_profit_margin?.value} unit="%" status={r.gross_profit_margin?.status} description="Gross Profit / Revenue" benchmark={data.benchmarks?.gross_profit_margin} />
                <RatioCard name="Net Margin" value={r.net_profit_margin?.value} unit="%" status={r.net_profit_margin?.status} description="Net Profit / Revenue" benchmark={data.benchmarks?.net_profit_margin} />
                <RatioCard name="Operating Margin" value={r.operating_margin?.value} unit="%" status={r.operating_margin?.status} description="Operating Income / Revenue" />
                <RatioCard name="ROA" value={r.return_on_assets?.value} unit="%" status={r.return_on_assets?.status} description="Net Profit / Total Assets" benchmark={data.benchmarks?.return_on_assets} />
                <RatioCard name="ROE" value={r.return_on_equity?.value} unit="%" status={r.return_on_equity?.status} description="Net Profit / Equity" benchmark={data.benchmarks?.return_on_equity} />
              </div>
            </div>

            {/* Leverage Ratios */}
            <div>
              <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                <TrendingDown size={16} className="text-error" /> Leverage Ratios
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <RatioCard name="Debt to Equity" value={r.debt_to_equity?.value} unit="x" status={r.debt_to_equity?.status} description="Total Liabilities / Equity" benchmark={data.benchmarks?.debt_to_equity} />
                <RatioCard name="Debt to Assets" value={r.debt_to_assets?.value} unit="x" status={r.debt_to_assets?.status} description="Total Liabilities / Total Assets" />
                <RatioCard name="Equity Ratio" value={r.equity_ratio?.value} unit="x" status={r.equity_ratio?.status} description="Equity / Total Assets" />
              </div>
            </div>

            {/* Efficiency Ratios */}
            <div>
              <h2 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
                <BarChart3 size={16} className="text-warning" /> Efficiency Ratios
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <RatioCard name="Inventory Turnover" value={r.inventory_turnover?.value} unit="x" status={r.inventory_turnover?.status} description="COGS / Avg Inventory" benchmark={data.benchmarks?.inventory_turnover} />
                <RatioCard name="AR Turnover" value={r.receivable_turnover?.value} unit="x" status={r.receivable_turnover?.status} description="Revenue / Receivables" />
                <RatioCard name="AP Turnover" value={r.payable_turnover?.value} unit="x" status={r.payable_turnover?.status} description="Expenses / Payables" />
                <RatioCard name="Asset Turnover" value={r.asset_turnover?.value} unit="x" status={r.asset_turnover?.status} description="Revenue / Total Assets" />
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
